import React, { useState, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import UploadIcon from '@mui/icons-material/Upload';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';

import CraftImage from './CraftImage';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import MyButton from '@common/components/Button/MyButton';
import Notification, {
  SEVERITY_OPTIONS,
  useNotification,
} from '@common/components/Notification/Notification';
import { TEMPLATES_PATHS } from '@common/network/ApiPaths';

import { postByPathAndData, deleteByPath } from '@services/newServices';
import { IMAGE_LIST_CONFIG, MEDIA_CONFIG } from '@common/constants';
import { selectBackgroundColor } from '@common/providers/ThemeProvider/selectors';

const StyledCheckbox = styled(Checkbox)(() => ({
  position: 'absolute',
  right: '0',
  color: '#d7c5c5',
}));

const Images = ({ imgs = [], fetchTemplateImages = () => {} }) => {
  const fileInputRef = useRef(null);
  const [checkedId, setCheckedId] = useState(null);
  const [showStatuses, showNotifications, handleClose] = useNotification();

  const { t } = useTranslation();

  const handleDelete = async () => {
    try {
      const response = await deleteByPath({
        path: TEMPLATES_PATHS.DELETE_MEDIA,
        pathVariables: { id: checkedId },
      });
      if (response.status === 200) {
        fetchTemplateImages();
        setCheckedId(null);
      }
    } catch (err) {
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('delete_failed'),
          show: true,
        },
      ]);
    }
  };

  const handleCheckbox = (event) => {
    const id = event.target.id;
    if (checkedId === id) {
      setCheckedId(null);
      return;
    }
    setCheckedId(id);
  };

  const onChange = async (event) => {
    const img = event.target.files[0];
    const formData = new FormData();
    formData.append('file', img);
    if (img) {
      try {
        if (img?.size > MEDIA_CONFIG.MAX_SIZE_IN_BYTES) {
          showNotifications([
            {
              severity: SEVERITY_OPTIONS.ERROR,
              message: t('file_too_large', {
                maxSize: MEDIA_CONFIG.MAX_SIZE_IN_MB,
              }),
              show: true,
            },
          ]);
          return;
        }

        const response = await postByPathAndData({
          path: TEMPLATES_PATHS.UPLOAD_MEDIA,
          data: formData,
        });
        if (response?.status === 201) {
          fetchTemplateImages();
        }
      } catch (err) {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.ERROR,
            message:
              err?.status === 413
                ? t('file_too_large', {
                    maxSize: MEDIA_CONFIG.MAX_SIZE_IN_MB,
                  })
                : t('upload_failed'),
            show: true,
          },
        ]);
      } finally {
        event.target.value = null;
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  // TODO - upload with database need to be implemented, as src must be provided for restore of image

  return (
    <>
      <SettingsWrapper>
        <Box
          sx={{
            display: 'inline',
          }}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            onChange={onChange}
            style={{ display: 'none' }}
          />
          <MyButton startIcon={<UploadIcon />} onClick={handleButtonClick}>
            {t('upload')}
          </MyButton>
        </Box>
        <MyButton
          sx={{
            ml: 1,
          }}
          variant='outlined'
          color='error'
          disabled={!checkedId}
          onClick={handleDelete}
        >
          {t('delete_btn').toUpperCase()}
        </MyButton>

        {imgs?.length ? (
          <ImageList
            sx={{
              overflowY: 'hidden',
              backgroundColor: selectBackgroundColor,
            }}
            cols={3}
            rowHeight={IMAGE_LIST_CONFIG.rowHeight}
          >
            {imgs?.map(({ thumbnail_url, id }, index) => (
              <ImageListItem key={index}>
                <CraftImage
                  src={`${thumbnail_url}?w=${IMAGE_LIST_CONFIG.rowWeight}&fit=${IMAGE_LIST_CONFIG.fit}&auto=${IMAGE_LIST_CONFIG.auto}`}
                  myIndex={index}
                />

                <StyledCheckbox
                  id={id}
                  checked={id === checkedId}
                  onChange={handleCheckbox}
                />
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          <Alert sx={{ mt: 1 }} severity='info'>
            {t('no_images')}
          </Alert>
        )}
      </SettingsWrapper>
      <Notification showStatuses={showStatuses} handleClose={handleClose} />
    </>
  );
};

export default Images;
